import React, { useState } from 'react';
import { useMutation, useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { Button } from 'antd';
import { DeleteFilled, EditOutlined, PlusOutlined } from '@ant-design/icons';
import Page from 'components/Page';
import Table from 'components/Table';
import { dateFormat, currencyFormat } from 'shared/utils/formatter';
import { DiscountType } from 'shared/prisma/types';
import EditCouponDrawer from './EditCouponDrawer';

const QUERY = gql`
  query GetCoupons {
    coupons {
      id
      code
      description
      discountAmount
      discountType
      expirationDate
      limitPerCustomer
    }
  }
`;

const DELETE_COUPON = gql`
  mutation DeleteCoupon($id: ID!) {
    deleteCoupon(id: $id) {
      id
    }
  }
`;

const columns = [
  {
    dataIndex: 'code',
    title: 'Code',
  },
  {
    dataIndex: 'description',
    title: 'Description',
  },
  {
    dataIndex: 'discountAmount',
    title: 'Discount',
    render: (amount, record) => (record.discountType === DiscountType.Percentage ? `${amount}%` : currencyFormat(amount)),
  },
  {
    dataIndex: 'expirationDate',
    title: 'Expiration Date',
    render: (date) => (date ? dateFormat(date) : 'No expiration'),
  },
  {
    dataIndex: 'limitPerCustomer',
    title: 'Limit / Account',
    render: (limit) => limit || '∞',
  },
];

const Coupons = () => {
  const { data, loading, refetch } = useQuery(QUERY);
  const [editCoupon, setEditCoupon] = useState(false);
  const [deleteCoupon] = useMutation(DELETE_COUPON, { onCompleted: refetch });

  const actions = (record) => (
    <Button type="primary" icon={<EditOutlined />} onClick={() => setEditCoupon(record)}>
      Edit
    </Button>
  );

  const actionsMenuItems = (record) => [
    {
      key: 'delete',
      icon: <DeleteFilled />,
      label: 'Delete',
      danger: true,
      onClick: () => deleteCoupon({ variables: { id: record.id } }),
    },
  ];

  return (
    <Page>
      <Page.Header title="Coupons" />
      <Page.Content>
        <Table
          loading={loading}
          columns={columns}
          dataSource={data?.coupons}
          actions={actions}
          actionsMenuItems={actionsMenuItems}
          footerActions={(
            <Button type="primary" onClick={() => setEditCoupon({})} icon={<PlusOutlined />}>
              Create Coupon
            </Button>
          )}
        />
        <EditCouponDrawer
          coupon={editCoupon}
          onDone={() => setEditCoupon(false)}
          onUpdate={refetch}
        />
      </Page.Content>
    </Page>
  );
};

export default Coupons;
